import {
  SpellcasterDTO,
  SoldiersDTO,
  SpellcastersAndSpellsDTO,
  WarbandMemberDTO,
  CreateSpellcasterRequest,
  CreateSoldierRequest,
  SoldierDTO,
  WarbandLevelUp,
  Weapon,
  Armour,
} from '../types';
import {
  loadWarbandData,
  saveWarbandData,
  StoredMember,
  StoredSoldier,
  buildHydrationMaps,
} from './localStorageService';
import { saveLevelUps as persistLevelUps } from './localStorageService';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const getRefMaps = () => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
  const s = useReferenceDataStore.getState();
  return buildHydrationMaps({
    weapons: s.weapons,
    armours: s.armours,
    buffs: s.buffs,
    treasureItems: s.treasureItems,
    baseResources: s.allBaseResources,
  });
};

function memberToDTO(stored: StoredMember, warbandId: string): SpellcasterDTO {
  const maps = getRefMaps();
  const weaponMap = maps.weaponMap;
  const armourMap = maps.armourMap;
  const buffMap = maps.buffMap;
  const treasureMap = maps.treasureMap;
  const countById = new Map<string, number>();
  for (const id of stored.buffIds) countById.set(id, (countById.get(id) ?? 0) + 1);
  return {
    id: stored.id,
    creatureId: stored.creatureId,
    warbandId,
    name: stored.name,
    currentHealth: stored.currentHealth,
    notes: stored.notes,
    isTemporary: stored.isTemporary,
    move: (stored as unknown as Record<string, number>).move ?? 6,
    fight: (stored as unknown as Record<string, number>).fight ?? 2,
    shoot: (stored as unknown as Record<string, number>).shoot ?? 0,
    armour: (stored as unknown as Record<string, number>).armour ?? 10,
    will: (stored as unknown as Record<string, number>).will ?? 4,
    health: (stored as unknown as Record<string, number>).health ?? 14,
    itemSlots: (stored as unknown as Record<string, number>).itemSlots ?? 5,
    creatureName: (stored as unknown as Record<string, string>).creatureName ?? stored.name,
    leftTable: stored.leftTable,
    carryingTreasure: stored.carryingTreasure,
    storedInInn: stored.storedInInn,
    buffsAndDebuffs: Array.from(countById.entries()).map(([id, count]) => {
      const buff = buffMap.get(id);
      return buff ? { ...buff, stackCount: count } : null;
    }).filter((b): b is NonNullable<typeof b> => b !== null),
    weapons: stored.weaponIds.map((id) => weaponMap.get(id)).filter((w): w is Weapon => w !== null && w !== undefined),
    armours: stored.armourIds.map((id) => armourMap.get(id)).filter((a): a is Armour => a !== null && a !== undefined),
    treasureItems: stored.treasureItemIds.map((id) => treasureMap.get(id)).filter((t): t is NonNullable<typeof t> => t !== null && t !== undefined),
  };
}

function soldierToDTO(stored: StoredSoldier, warbandId: string): SoldierDTO {
  const base = memberToDTO(stored, warbandId);
  return { ...base, armours: base.armours ?? [], type: stored.type, cost: stored.cost };
}

function makeStoredMember(
  memberId: string,
  creatureId: string,
  name: string,
  creatureName: string,
  weapons: Array<{ id: string }>,
  armours: Array<{ id: string }>,
  health: number,
  move: number,
  fight: number,
  shoot: number,
  armour: number,
  will: number,
  itemSlots: number,
): StoredMember {
  return {
    id: memberId,
    creatureId,
    name,
    currentHealth: health,
    notes: null,
    isTemporary: false,
    weaponIds: weapons.map((w) => w.id),
    armourIds: armours.map((a) => a.id),
    buffIds: [],
    treasureItemIds: [],
    storedInInn: 0,
    leftTable: false,
    carryingTreasure: false,
    // Stored stats
    move,
    fight,
    shoot,
    armour,
    will,
    health,
    itemSlots,
    creatureName,
  } as unknown as StoredMember;
}

export const manageWarbandService = {
  getWizard: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    if (!data?.wizard) return Promise.resolve({ data: null as SpellcasterDTO | null });
    return Promise.resolve({ data: memberToDTO(data.wizard, warbandId) });
  },

  healWizardToFullHealth: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    if (!data?.wizard) return Promise.resolve({ data: null as SpellcasterDTO | null });
    const maxHealth = (data.wizard as unknown as Record<string, number>).health ?? 14;
    data.wizard = { ...data.wizard, currentHealth: maxHealth } as StoredMember;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(data.wizard, warbandId) });
  },

  createWizard: (warbandId: string, request: CreateSpellcasterRequest) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as SpellcasterDTO | null });
    const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
    const wizard = useReferenceDataStore.getState().creatures.find((c) => c.id === 'wizard');
    const stored = makeStoredMember(
      crypto.randomUUID(), 'wizard', request.name, wizard?.name ?? 'Wizard', request.weapons, request.armours,
      wizard?.health ?? 14, wizard?.move ?? 6, wizard?.fight ?? 2, wizard?.shoot ?? 0,
      wizard?.armour ?? 10, wizard?.will ?? 4, wizard?.itemSlot ?? 5,
    );
    data.wizard = stored;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(stored, warbandId) });
  },

  replaceWizardWeapons: (warbandId: string, request: CreateSpellcasterRequest) => {
    const data = loadWarbandData(warbandId);
    if (!data?.wizard) return Promise.resolve({ data: null as SpellcasterDTO | null });
    data.wizard = { ...data.wizard, weaponIds: request.weapons.map((w) => w.id) } as StoredMember;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(data.wizard, warbandId) });
  },

  getApprentice: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    if (!data?.apprentice) return Promise.resolve({ data: null as SpellcasterDTO | null });
    return Promise.resolve({ data: memberToDTO(data.apprentice, warbandId) });
  },

  healApprenticeToFullHealth: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    if (!data?.apprentice) return Promise.resolve({ data: null as SpellcasterDTO | null });
    const maxHealth = (data.apprentice as unknown as Record<string, number>).health ?? 14;
    data.apprentice = { ...data.apprentice, currentHealth: maxHealth } as StoredMember;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(data.apprentice, warbandId) });
  },

  createApprentice: (warbandId: string, request: CreateSpellcasterRequest) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as SpellcasterDTO | null });
    const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
    const wizard = useReferenceDataStore.getState().creatures.find((c) => c.id === 'wizard');
    const stored = makeStoredMember(
      crypto.randomUUID(), 'apprentice', request.name, 'Apprentice', request.weapons, request.armours,
      wizard?.health ?? 14, wizard?.move ?? 6, wizard?.fight ?? 2, wizard?.shoot ?? 0,
      wizard?.armour ?? 10, wizard?.will ?? 4, wizard?.itemSlot ?? 5,
    );
    data.apprentice = stored;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(stored, warbandId) });
  },

  replaceApprenticeWeapons: (warbandId: string, request: CreateSpellcasterRequest) => {
    const data = loadWarbandData(warbandId);
    if (!data?.apprentice) return Promise.resolve({ data: null as SpellcasterDTO | null });
    data.apprentice = { ...data.apprentice, weaponIds: request.weapons.map((w) => w.id) } as StoredMember;
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: memberToDTO(data.apprentice, warbandId) });
  },

  getWarbandSpellcasters: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const { spellService } = require('./spellService') as typeof import('./spellService');
    return spellService.getAllForWarband(warbandId).then((spellsRes) => ({
      data: {
        wizard: data?.wizard ? memberToDTO(data.wizard, warbandId) : null,
        apprentice: data?.apprentice ? memberToDTO(data.apprentice, warbandId) : null,
        warbandSpells: spellsRes.data ?? [],
      } as SpellcastersAndSpellsDTO,
    }));
  },

  getSoldiers: (warbandId: string) => {
    const data = loadWarbandData(warbandId);
    const soldiers = (data?.soldiers ?? []).map((s) => soldierToDTO(s, warbandId));
    return Promise.resolve({ data: { soldiers } as SoldiersDTO });
  },

  createSoldier: (warbandId: string, request: CreateSoldierRequest) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null });
    const { useReferenceDataStore } = require('../stores/referenceDataStore') as typeof import('../stores/referenceDataStore');
    const creature = useReferenceDataStore.getState().creatures.find((c) => c.id === request.creatureId);
    const stored: StoredSoldier = {
      id: crypto.randomUUID(),
      creatureId: request.creatureId,
      name: request.name,
      currentHealth: request.currentHealth,
      notes: request.notes ?? null,
      isTemporary: false,
      weaponIds: (creature?.weapons ?? []).map((w) => w.id),
      armourIds: (creature?.armourItems ?? []).map((a) => a.id),
      buffIds: [],
      treasureItemIds: [],
      storedInInn: 0,
      leftTable: false,
      carryingTreasure: false,
      type: creature?.type ?? 'StandardSoldier',
      cost: creature?.cost ?? 0,
      move: creature?.move ?? 6,
      fight: creature?.fight ?? 2,
      shoot: creature?.shoot ?? 0,
      armour: creature?.armour ?? 10,
      will: creature?.will ?? 0,
      health: creature?.health ?? 10,
      itemSlots: creature?.itemSlot ?? 1,
      creatureName: creature?.name ?? request.name,
    } as unknown as StoredSoldier;
    data.soldiers = [...data.soldiers, stored];
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: soldierToDTO(stored, warbandId) });
  },

  deleteSoldier: (warbandId: string, warbandMemberId: string) => {
    const data = loadWarbandData(warbandId);
    if (data) {
      data.soldiers = data.soldiers.filter((s) => s.id !== warbandMemberId);
      saveWarbandData(warbandId, data);
    }
    return Promise.resolve({ data: undefined });
  },

  updateSoldier: (warbandId: string, warbandMemberId: string, updateData: Partial<WarbandMemberDTO>) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as WarbandMemberDTO | null });
    const idx = data.soldiers.findIndex((s) => s.id === warbandMemberId);
    if (idx === -1) return Promise.resolve({ data: null as WarbandMemberDTO | null });
    data.soldiers[idx] = { ...data.soldiers[idx], ...(updateData as unknown as Partial<StoredSoldier>) };
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: soldierToDTO(data.soldiers[idx], warbandId) as WarbandMemberDTO });
  },

  healSoldierToFullHealth: (warbandId: string, warbandMemberId: string, _currentHealth: number) => {
    const data = loadWarbandData(warbandId);
    if (!data) return Promise.resolve({ data: null as SoldierDTO | null });
    const idx = data.soldiers.findIndex((s) => s.id === warbandMemberId);
    if (idx === -1) return Promise.resolve({ data: null as SoldierDTO | null });
    const maxHealth = (data.soldiers[idx] as unknown as Record<string, number>).health ?? 10;
    data.soldiers[idx] = { ...data.soldiers[idx], currentHealth: maxHealth };
    saveWarbandData(warbandId, data);
    return Promise.resolve({ data: soldierToDTO(data.soldiers[idx], warbandId) });
  },

  saveLevelUps: (warbandId: string, levelUpData: { experience: number; levelUps: WarbandLevelUp[] }) => {
    persistLevelUps(warbandId, levelUpData.levelUps, levelUpData.experience);
    return Promise.resolve({ data: undefined });
  },
};
